import { dateFromDateString, getDateKey, getDateString, getNormalizedDate, jsonDateReviver } from './dateUtils';
import { LetterEvaluation, SecureTargetWord } from './wordUtils';
import { storageWriter } from './storageManager';

export interface GameState {
  guesses: string[];
  evaluations: LetterEvaluation[][];
  targetWord: SecureTargetWord;
  targetWordNumber: number | null;
  targetWordDate: Date | null; // Date of the target word
  gameStatus: GameStatus;
  currentRowIndex: number;
  keyboardState: Record<string, LetterEvaluation | undefined>;
  lastCompletedTime: number | null;
  error: string | null;
}

export interface GameHistory {
  lastCompletedDate?: string;
  hintLetter?: string;
  hintLetterDate?: string;
  hasPlayedBefore?: boolean;
  completions?: Record<string, GameStatus>;
}

export interface GameConfig {
  mode: GameMode;
  date: Date | null;
}

export interface GameSettings {
  wordLength: number;
  maxAttempts: number;
}

export const GAME_SETTINGS: GameSettings = {
  wordLength: 4,
  maxAttempts: 6
};

export enum GameMode {
  RANDOM = 'random',
  DAILY = 'daily',
  ENCORE = 'encore',
  ARCHIVE = 'archive'
}

export enum GameStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  WIN = 'WIN',
  LOSE = 'LOSE'
}

// Constants for the game
export const ENCORE_TIMER_SECONDS = 15; // Easily editable constant for the timer
export const FIRST_ROW_INDEX = 0;
export const LAST_ROW_INDEX = GAME_SETTINGS.maxAttempts - 1;

const GAME_STATE_KEY_PREFIX = 'gameState';
const HISTORY_KEY_PREFIX = 'gameHistory';
export const GAME_CONFIG_KEY = 'gameConfig';

// Helper to check if we should use a date key (for daily and archive modes)
const shouldUseDateKey = (mode: GameMode): boolean => {
  return (mode === GameMode.DAILY || mode === GameMode.ARCHIVE);
};

export const craftGameStorageKey = (mode: GameMode, date?: Date): string => {
  // If the mode is daily and it's not today, add the date to the key
  const datePostfix = shouldUseDateKey(mode) ? `:${getDateKey(date)}` : '';
  const modePrefix = mode === GameMode.ARCHIVE ? GameMode.DAILY : mode;
  return `${GAME_STATE_KEY_PREFIX}:${modePrefix}${datePostfix}`;
};

export const craftHistoryStorageKey = (mode: GameMode): string => {
  const modePrefix = mode === GameMode.ARCHIVE ? GameMode.DAILY : mode;
  return `${HISTORY_KEY_PREFIX}:${modePrefix}`;
};

export const saveGame = (gameState: GameState, mode: GameMode, date?: Date): void => {
  const key = craftGameStorageKey(mode, date);
  const storableState = {
    ...gameState,
    targetWordDate: gameState.targetWordDate ? getDateString(gameState.targetWordDate) : null,
  };
  // Use debounced writer instead of direct localStorage.setItem
  storageWriter.queueWrite(key, storableState);
};

export const loadGame = (mode: GameMode, date?: Date): GameState | null => {
  const key = craftGameStorageKey(mode, date);
  const savedState = localStorage.getItem(key);

  if (savedState !== null) {
    try {
      return JSON.parse(savedState, jsonDateReviver) as GameState;
    } catch (e) {
      console.error('Error parsing saved game state:', e);
      return null;
    }
  }

  return null;
};

export const clearGame = (mode: GameMode, date?: Date): void => {
  const key = craftGameStorageKey(mode, date);
  localStorage.removeItem(key);
};

export const saveGameConfig = (config: GameConfig): void => {
  const storableConfig = {
    ...config,
    date: config.date ? getDateString(config.date) : null,
  };
  storageWriter.queueWrite(GAME_CONFIG_KEY, storableConfig);
};

export const loadGameConfig = (): GameConfig => {
  const rawConfig = localStorage.getItem(GAME_CONFIG_KEY);
  if (rawConfig) {
    try {
      return JSON.parse(rawConfig, jsonDateReviver) as GameConfig;
    } catch (e) {
      console.error('Error parsing saved game config:', e);
    }
  }

  return { mode: GameMode.DAILY, date: getNormalizedDate() };
};

export const loadHistory = (mode: GameMode): GameHistory | null => {
  let key = craftHistoryStorageKey(mode);
  let savedHistory = localStorage.getItem(key);

  if (savedHistory !== null) {
    try {
      return JSON.parse(savedHistory);
    } catch (e) {
      console.error('Error parsing saved game state:', e);
      return null;
    }
  }

  return null;
};

export const saveHistory = (gameHistory: GameHistory, mode: GameMode): void => {
  const priorHistory = loadHistory(mode);
  const key = craftHistoryStorageKey(mode);
  
  localStorage.setItem(key, JSON.stringify({
    ...priorHistory,
    ...gameHistory
  }));
};

export const saveCompletion = (mode: GameMode, targetWordDate: Date, completionStatus: GameStatus): void => {
  const priorHistory = loadHistory(mode);
  const priorCompletions = loadCompletions(mode);

  const updatedHistory = {
    ...priorHistory,
    completions: {
      ...priorCompletions,
      [getDateString(getNormalizedDate(targetWordDate))]: completionStatus
    }
  };

  saveHistory(updatedHistory, mode);
};

export const loadCompletions = (mode: GameMode): Record<string, GameStatus> => {
  const history = loadHistory(mode);
  return history?.completions ?? {};
};

export const saveCompletedDate = (
  mode: GameMode,
  completionStatus: GameStatus,
  providedCompletionDate?: Date,
  providedGameDate?: Date
): void => {
  const completionDate = providedCompletionDate ?? getNormalizedDate(new Date());
  const targetWordDate = providedGameDate ?? getNormalizedDate(new Date());

  saveHistory({
    lastCompletedDate: getDateString(completionDate)
  }, mode);

  saveCompletion(mode, targetWordDate, completionStatus);
};

export const loadCompletedDate = (mode: GameMode): string | null => {
  const history = loadHistory(mode);
  return history?.lastCompletedDate || null;
};

export const isValidToPlayEncore = (
  dailyHistory: GameHistory,
  encoreHistory: GameHistory
): boolean => {
  const todayString = getDateString(getNormalizedDate());
  const dailyCompletedToday = dailyHistory.completions?.[todayString] !== undefined;
  const encoreCompletedToday = encoreHistory.completions?.[todayString] !== undefined;

  return dailyCompletedToday && !encoreCompletedToday;
};

export const saveHintLetter = (letter: string, date: Date, mode: GameMode = GameMode.DAILY): void => {
  if (letter.length !== 1) return;

  saveHistory({
    hintLetter: letter.toLowerCase(),
    hintLetterDate: getDateString(date)
  }, mode)
};

export const loadHintLetter = (mode: GameMode = GameMode.DAILY): string | null => {
  const history = loadHistory(mode);
  return history?.hintLetter || null;
};

export const loadHintLetterDate = (mode: GameMode = GameMode.DAILY): string | null => {
  const history = loadHistory(mode);
  return history?.hintLetterDate || null;
};

export const clearHintLetter = (mode: GameMode = GameMode.DAILY): void => {
  saveHistory({
    hintLetter: null,
    hintLetterDate: null
  }, mode)
};

export const loadHasPlayedBefore = (): boolean => {
  const history = loadHistory(GameMode.RANDOM); // Using random for all hasPlayed history for now
  return history?.hasPlayedBefore || false;
};

export const saveHasPlayedBefore = (hasPlayed: boolean): void => {
  saveHistory({
    hasPlayedBefore: hasPlayed
  }, GameMode.RANDOM) // Using random for all hasPlayed history for now
};
